<template>
  <div class="c-list w-full flex flex-col">
    <div class="overflow-hidden c-list__content">
      <div v-if="listDataWithIndices?.length === 0" class="flex py-8 justify-center h-full">
        <span>{{ t('canvasUI.common.noData') }}</span>
      </div>
      <div
        v-else
        ref="virtualizerContainerRef"
        class="c-list__virtualizer-container overflow-y-auto contain-strict"
        :style="{ height: `${virtualizerContainerHeight}px` }"
      >
        <div
          v-if="virtualRows?.length"
          class="c-list__virtualizer relative w-full"
          :style="{ height: `${totalVirtualizerHeight}px` }"
        >
          <div
            v-for="{ key, index, start } in virtualRows"
            :key="`${key}`"
            :ref="measureElement"
            class="w-full absolute top-0 left-0"
            :data-index="index"
            :style="{
              transform: `translateY(${start}px)`,
            }"
          >
            <CListItem
              class="cursor-pointer"
              :context-menu-x="contextMenuItemId === listDataWithIndices[index].id ? contextMenuX : undefined"
              :context-menu-y="contextMenuItemId === listDataWithIndices[index].id ? contextMenuY : undefined"
              :index="index"
              :is-context-menu-open="contextMenuItemId === listDataWithIndices[index].id"
              :item="listDataWithIndices[index]"
              :movable="movable"
              :selected-ids="selectedIds ?? []"
              @click="handleItemClick"
              @context-menu="handleContextMenuChange"
              @dragging="handleDragging"
              @move="emit('move', $event)"
            >
              <template #default="{ item, index, isSelected, isDraggedOver, isContextMenuOpen }">
                <slot
                  :index="index"
                  :isContextMenuOpen="isContextMenuOpen"
                  :isDraggedOver="isDraggedOver"
                  :isSelected="isSelected"
                  :item="item"
                  name="item"
                />
              </template>
              <template #item:context-menu="{ item }">
                <slot :item="item" name="item:context-menu" />
              </template>
            </CListItem>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted, reactive, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVirtualizer } from '@tanstack/vue-virtual'
import { debounce } from 'lodash-es'

import CListItem from './CListItem.vue'

export interface ListDataItem {
  id: number | string
  [key: string]: any
}

const props = defineProps<{
  data: ListDataItem[]
  selectedIds?: (number | string)[]
  maxRowHeight: number
  height?: number
  movable?: boolean
}>()

const emit = defineEmits(['item:click', 'move'])
const { t } = useI18n()
const contentContainer = ref<any | null>(null)
const contextMenuItemId = ref<number | string | null>(null)
const contextMenuX = ref(0)
const contextMenuY = ref(0)
const listDataWithIndices = computed(() => props.data?.map((item, index) => ({ ...item, index })))

function handleContextMenuChange(item: ListDataItem, isOpen: boolean, e?: MouseEvent) {
  if (isOpen && e) {
    contextMenuItemId.value = item.id
    contextMenuX.value = e.clientX + 200 > window.innerWidth ? e.clientX - 215 : e.clientX
    contextMenuY.value = e.clientY
  } else {
    contextMenuItemId.value = null
  }
}

function handleItemClick(item: ListDataItem, event: MouseEvent) {
  if (contextMenuItemId.value) {
    contextMenuItemId.value = null
    return
  }
  emit('item:click', item, event)
}

function handleDragging(isDragging: boolean) {
  if (isDragging) {
    contentContainer.value?.$el?.addEventListener?.('mousemove', handleMouseMove)
  } else {
    contentContainer.value?.$el?.removeEventListener?.('mousemove', handleMouseMove)
  }
}

function handleMouseMove() {
  // just to trigger scroll behavior
}

const windowDimensions = reactive({
  height: window.innerHeight,
  width: window.innerWidth,
})

const virtualizerContainerRef = ref<HTMLElement | null>(null)
const virtualizerContainerHeight = computed(() => {
  if (!virtualizerContainerRef.value || props.height) return props.height ?? 400 // sensible fallback height

  const offsetTop = virtualizerContainerRef.value.getBoundingClientRect().top
  return windowDimensions.height - offsetTop
})
const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: props.data?.length,
    getScrollElement: () => virtualizerContainerRef.value,
    estimateSize: () => props.maxRowHeight,
    overscan: 5,
  })),
)
const measureElement = (el: Element) => {
  if (!el || !rowVirtualizer.value) return undefined

  nextTick(() => {
    rowVirtualizer.value.measureElement(el)
  })
  return undefined
}
const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems())
const totalVirtualizerHeight = computed(() => rowVirtualizer.value.getTotalSize())

const onResize = debounce(() => {
  windowDimensions.height = window.innerHeight
  windowDimensions.width = window.innerWidth
}, 200)

onMounted(() => {
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})

defineExpose({
  updateContextMenuItem: handleContextMenuChange,
})
</script>
